import React from 'react';
import { Card, Button, Upload, Space, Tag, Progress, Typography, message } from 'antd';
import { PlusOutlined, DeleteOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { TaskDraftReport } from '../../services/tasks';

const { Text } = Typography;

interface User {
  id: number;
  name: string;
  email: string;
}

interface DraftTabProps {
  canEdit: boolean;
  canDelete: boolean;
  canVerify: boolean;
  draftReports: Array<TaskDraftReport | { id: string; task_id: number; attachment: null; enabled: boolean; created_at: string; creator?: User; file: File; batch_no?: number; approval_status?: 'pending' | 'approved' | 'rejected' }>;
  uploadQueue: Array<{ file: File; id: string }>;
  uploadStatus: Record<string, 'pending' | 'uploading' | 'success' | 'error'>;
  uploadProgress: Record<string, number>;
  handleAddDraftReport: (file: File, batchNo?: number) => void;
  handleDeleteDraftReport: (reportId: number | string) => void;
  handleBatchStatusChange: (batchNo: number, status: 'approved' | 'rejected') => void;
  formatDateTime: (dateString?: string) => string;
}

export const DraftTab: React.FC<DraftTabProps> = ({
  canEdit,
  canDelete,
  canVerify,
  draftReports,
  uploadQueue,
  uploadStatus,
  uploadProgress,
  handleAddDraftReport,
  handleDeleteDraftReport,
  handleBatchStatusChange,
  formatDateTime,
}) => {
  const allowDelete = canDelete && canEdit;
  const normalizeBatchNo = (report: any) => report?.batch_no || 1;
  const getBatchReports = (batchNo: number) =>
    draftReports.filter((report) => normalizeBatchNo(report) === batchNo);
  const getBatchStatus = (reports: any[]) => {
    if (!reports.length) {
      return 'pending';
    }
    const latest = [...reports].sort((a, b) => {
      const aTime = new Date(a.created_at || 0).getTime();
      const bTime = new Date(b.created_at || 0).getTime();
      return bTime - aTime;
    })[0];
    return latest?.approval_status || 'pending';
  };
  const getStatusTag = (status: 'pending' | 'approved' | 'rejected') => {
    if (status === 'approved') return <Tag color="green">Approved</Tag>;
    if (status === 'rejected') return <Tag color="red">Rejected</Tag>;
    return <Tag color="orange">Pending</Tag>;
  };
  const renderUploadButton = (batchNo: number, disabled?: boolean) => (
    <Upload
      multiple
      beforeUpload={(file) => {
        handleAddDraftReport(file, batchNo);
        return false;
      }}
      showUploadList={false}
      disabled={disabled}
    >
      <Button type="link" icon={<PlusOutlined />} disabled={disabled}>
        Add
      </Button>
    </Upload>
  );
  const updateBatchStatus = async (batchNo: number, status: 'approved' | 'rejected') => {
    const batchReports = getBatchReports(batchNo);
    if (batchReports.length === 0) {
      return;
    }
    try {
      const updates = batchReports
        .filter((report) => typeof report.id === 'number')
        .map((report) => api.put(`/task-draft-reports/${report.id}`, { approval_status: status }));
      if (updates.length > 0) {
        await Promise.all(updates);
      }
      handleBatchStatusChange(batchNo, status);
      message.success(`Batch ${batchNo} ${status}`);
    } catch (error: any) {
      message.error('Failed to update approval status');
    }
  };
  const renderStatusActions = (batchNo: number, status: 'pending' | 'approved' | 'rejected', hasReports: boolean) => {
    if (!canVerify || !hasReports || status !== 'pending') {
      return null;
    }
    return (
      <Space size={6}>
        <Button size="small" onClick={() => updateBatchStatus(batchNo, 'approved')}>
          Approve
        </Button>
        <Button size="small" danger onClick={() => updateBatchStatus(batchNo, 'rejected')}>
          Reject
        </Button>
      </Space>
    );
  };
  const renderReportList = (reports: DraftTabProps['draftReports']) => {
    if (!reports.length) {
      return (
        <div style={{ color: '#999', textAlign: 'center', padding: '12px 0' }}>
          No draft reports
        </div>
      );
    }

    return reports.map((report) => {
      const reportId: number | string = report.id;
      const isTemp = typeof reportId === 'string' && reportId.startsWith('temp-draft-');
      const fileName = isTemp
        ? ('file' in report ? (report as any).file?.name : 'New file')
        : (report.attachment ? report.attachment.split('/').pop() : 'No attachment');

      const handleView = async () => {
        if (isTemp || !report.attachment) return;
        try {
          const response = await api.get(`/task-draft-reports/${report.id}/view`, {
            responseType: 'blob',
          });
          const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
          const blobUrl = window.URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
        } catch (error: any) {
          message.error('Failed to view file');
        }
      };

      const handleDownload = async () => {
        if (isTemp || !report.attachment) return;
        try {
          const response = await api.get(`/task-draft-reports/${report.id}/download`, {
            responseType: 'blob',
          });
          const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName || 'draft-report';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
        } catch (error: any) {
          message.error('Failed to download file');
        }
      };

      return (
        <div key={report.id} style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4 }}>
            {isTemp ? (
              <Space>
                <Text>{fileName}</Text>
                <Tag color="orange">Pending</Tag>
                {allowDelete && (
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteDraftReport(report.id!)}
                    size="small"
                  />
                )}
              </Space>
            ) : report.attachment ? (
              <Space>
                <Button
                  type="link"
                  icon={<EyeOutlined />}
                  disabled={false}
                  onClick={handleView}
                  size="small"
                >
                  {fileName}
                </Button>
                <Button
                  type="link"
                  icon={<DownloadOutlined />}
                  disabled={false}
                  onClick={handleDownload}
                  size="small"
                />
                {allowDelete && (
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteDraftReport(report.id!)}
                    size="small"
                  />
                )}
              </Space>
            ) : (
              <Text strong>No attachment</Text>
            )}
          </div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            By {report.creator?.name || 'Unknown'} {formatDateTime(report.created_at)}
          </div>
        </div>
      );
    });
  };
  const batch1Reports = getBatchReports(1);
  const batch2Reports = getBatchReports(2);
  const batch3Reports = getBatchReports(3);
  const batch1Status = getBatchStatus(batch1Reports) as 'pending' | 'approved' | 'rejected';
  const batch2Status = getBatchStatus(batch2Reports) as 'pending' | 'approved' | 'rejected';
  const batch3Status = getBatchStatus(batch3Reports) as 'pending' | 'approved' | 'rejected';
  const allowBatch2 = batch1Status === 'rejected';
  const allowBatch3 = batch2Status === 'rejected';
  const showBatch2 = allowBatch2 || batch2Reports.length > 0;
  const showBatch3 = allowBatch3 || batch3Reports.length > 0;
  return (
    <Card
      title="Draft Reports"
      bodyStyle={{ padding: '16px' }}
    >
      {/* Upload Progress */}
      {uploadQueue.length > 0 && (
        <div style={{ marginBottom: 16, padding: '12px', background: '#fafafa', borderRadius: 4 }}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Uploading {uploadQueue.length} file(s)</Text>
          </div>
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {uploadQueue.map((item) => {
              const status = uploadStatus[item.id] || 'pending';
              const progress = uploadProgress[item.id] || 0;
              return (
                <div key={item.id} style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text style={{ fontSize: '12px' }} ellipsis>
                      {item.file.name}
                    </Text>
                    <Tag
                      color={
                        status === 'success' ? 'green' :
                        status === 'error' ? 'red' :
                        status === 'uploading' ? 'blue' : 'default'
                      }
                    >
                      {status === 'success' ? 'Done' :
                       status === 'error' ? 'Failed' :
                       status === 'uploading' ? 'Uploading' : 'Pending'}
                    </Tag>
                  </div>
                  {status === 'uploading' && (
                    <Progress percent={progress} size="small" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8 }}>
          <Space size={8}>
            {getStatusTag(batch1Status)}
            {renderStatusActions(1, batch1Status, batch1Reports.length > 0)}
          </Space>
          {canEdit ? renderUploadButton(1, batch1Status !== 'pending') : null}
        </div>
        {renderReportList(batch1Reports)}
      </div>
      {showBatch2 && (
        <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8 }}>
            <Space size={8}>
              {getStatusTag(batch2Status)}
              {renderStatusActions(2, batch2Status, batch2Reports.length > 0)}
            </Space>
            {canEdit ? renderUploadButton(2, !allowBatch2 || batch2Status !== 'pending') : null}
          </div>
          {renderReportList(batch2Reports)}
        </div>
      )}
      {showBatch3 && (
        <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8 }}>
            <Space size={8}>
              {getStatusTag(batch3Status)}
              {renderStatusActions(3, batch3Status, batch3Reports.length > 0)}
            </Space>
            {canEdit ? renderUploadButton(3, !allowBatch3 || batch3Status !== 'pending') : null}
          </div>
          {renderReportList(batch3Reports)}
        </div>
      )}
    </Card>
  );
};
